import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import SummaryCards from '../../components/invoice/SummaryCards';
import ComponentBreakdownChart from '../../components/invoice/ComponentBreakdownChart';
import TopEnterprisesChart from '../../components/invoice/TopEnterprisesChart';
import EnterpriseTable from '../../components/invoice/EnterpriseTable';
import GenerateAllModal from '../../components/invoice/GenerateAllModal';
import { useToast } from '../../context/ToastContext';
import invoiceService from '../../services/invoiceService';
import { getMonthOptions, formatMonthLabel, formatCurrencyAbbrev, formatCurrency } from '../../utils/invoiceFormatters';

const InvoiceDashboard = ({ source = 'ECMS' }) => {
  const navigate = useNavigate();
  const { error: toastError, success: toastSuccess, info: toastInfo } = useToast();
  const showToast = (msg, type) => {
    if (type === 'success') toastSuccess(msg);
    else if (type === 'error') toastError(msg);
    else toastInfo(msg);
  };
  const monthOptions = getMonthOptions();
  // Default: previous month
  const defaultOpt = monthOptions[1] || monthOptions[0];
  const [selectedValue, setSelectedValue] = useState(defaultOpt?.value || '');
  const selectedOpt = monthOptions.find(o => o.value === selectedValue) || defaultOpt;

  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [checkingJob, setCheckingJob] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!selectedOpt) return;
    setSummaryLoading(true);
    setSummaryError(false);
    try {
      const res = await invoiceService.getSummary(selectedOpt.month, selectedOpt.year, source);
      setSummaryData(res.data);
    } catch {
      setSummaryError(true);
    } finally {
      setSummaryLoading(false);
    }
  }, [selectedOpt?.month, selectedOpt?.year, source]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleExportCSV = async () => {
    if (!selectedOpt) return;
    setExporting(true);
    try {
      const res = await invoiceService.getInvoices({
        month: selectedOpt.month,
        year: selectedOpt.year,
        source,
        page: 0,
        size: 1000,
      });
      const invoices = res.data?.content || [];
      if (invoices.length === 0) {
        showToast('No invoices found for the selected month', 'warning');
        return;
      }
      const headers = [
        'Invoice Number', 'Enterprise Name', 'Enterprise Created On', 'Enterprise ID',
        'Month', 'Year',
        'Maker Count', 'Per User Per Month MMG (₹)', 'Maker Charge (₹)',
        'Admin Count', 'Admin Charge (₹)',
        'UPI Transactions', 'UPI Charge (₹)',
        'Loading Charge (₹)', 'Loading Charges MMG (₹)', 'Total Load Amount (₹)',
        'Additional Charge (₹)', 'Per Month Overall MMG (₹)',
        'Total Amount (₹)',
        'Status', 'Generated By', 'Generated At',
      ];
      const rows = invoices.map(inv => [
        inv.invoiceNumber || '',
        inv.enterpriseName || '',
        inv.enterpriseCreatedOn ? new Date(inv.enterpriseCreatedOn).toLocaleDateString('en-IN') : '',
        inv.enterpriseId || '',
        inv.invoiceMonth || '',
        inv.invoiceYear || '',
        inv.makerCount ?? '',
        inv.perUserMonthlyMmg ?? '',
        inv.makerCharge ?? '',
        inv.adminCount ?? '',
        inv.adminCharge ?? '',
        inv.upiTxnCount ?? '',
        inv.upiCharge ?? '',
        inv.loadingCharge ?? '',
        inv.perMonthLoadingMmg ?? '',
        inv.totalLoadAmount ?? '',
        inv.additionalCharge ?? '',
        inv.perMonthOverallMmg ?? '',
        inv.totalAmount ?? '',
        inv.status || '',
        inv.generatedByName || '',
        inv.generatedAt ? new Date(inv.generatedAt).toLocaleString('en-IN') : '',
      ]);
      const csvContent = [headers, ...rows]
        .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${source.toLowerCase()}_billing_${selectedOpt.year}_${String(selectedOpt.month).padStart(2, '0')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast(`Exported ${invoices.length} invoices`, 'success');
    } catch {
      showToast('Failed to export CSV. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleOpenModal = async () => {
    setCheckingJob(true);
    try {
      const res = await invoiceService.getLatestJob();
      if (res.data && res.data.status === 'RUNNING') {
        showToast('Generation already in progress. Please check generation history for status.', 'warning');
        return;
      }
      setShowModal(true);
    } catch (e) {
      // 204 No Content means no jobs yet — safe to open
      if (e.response?.status === 204 || e.response?.status === 404) {
        setShowModal(true);
      } else {
        setShowModal(true); // still open if check fails
      }
    } finally {
      setCheckingJob(false);
    }
  };

  const handleConfirmGenerate = async (month, year) => {
    setShowModal(false);
    try {
      const res = await invoiceService.bulkGenerate(month, year, source);
      const jobId = res.data.id;
      navigate(`/admin/${source.toLowerCase()}/invoices/jobs/${jobId}`);
    } catch (e) {
      const msg = e.response?.data?.error || 'Generation failed. Please try again.';
      showToast(msg, 'error');
    }
  };

  const subtitle = selectedOpt
    ? `${summaryData?.invoicedCount ?? '…'} of ${summaryData?.totalEnterprises ?? '…'} enterprises invoiced — ${formatMonthLabel(selectedOpt.month, selectedOpt.year)}`
    : '';

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="w-full px-4 py-8">

        {/* Top bar */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{source === 'IFLEET' ? 'iFleet Billing' : 'ECMS Billing'}</h1>
            <p className="text-gray-500 text-sm mt-0.5">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selectedValue}
              onChange={e => setSelectedValue(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white">
              {monthOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              onClick={() => navigate(`/admin/${source.toLowerCase()}/invoices/jobs`)}
              className="px-4 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 bg-white transition-colors">
              Generation History
            </button>
            <button
              onClick={handleExportCSV}
              disabled={exporting}
              className="px-4 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 bg-white transition-colors disabled:opacity-60 flex items-center gap-1.5">
              {exporting && (
                <span className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              )}
              Export CSV
            </button>
            <button
              onClick={handleOpenModal}
              disabled={checkingJob}
              className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-light transition-colors disabled:opacity-60 flex items-center gap-1.5">
              {checkingJob && (
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              Generate All Billing
            </button>
          </div>
        </div>

        {/* Enterprise & Invoice Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          {[
            {
              label: 'Total Enterprises',
              value: summaryLoading ? '—' : (summaryData?.totalEnterprises ?? 0),
              sub: 'registered',
              color: '#1F2937',
            },
            {
              label: 'Billed This Month',
              value: summaryLoading ? '—' : (summaryData?.invoicedCount ?? 0),
              sub: 'enterprises billed',
              color: '#0C447C',
            },
            {
              label: 'Not Yet Billed',
              value: summaryLoading ? '—' : Math.max(0, (summaryData?.totalEnterprises ?? 0) - (summaryData?.invoicedCount ?? 0)),
              sub: 'pending billing',
              color: '#A32D2D',
            },
            {
              label: 'Revenue This Month',
              value: summaryLoading ? '—' : formatCurrencyAbbrev(summaryData?.totalBilled),
              sub: summaryLoading ? '' : formatCurrency(summaryData?.totalBilled),
              color: '#27500A',
            },
            {
              label: 'Pending Collection',
              value: summaryLoading ? '—' : formatCurrencyAbbrev(summaryData?.pendingAmount),
              sub: summaryLoading ? '' : formatCurrency(summaryData?.pendingAmount),
              color: '#B45309',
            },
            {
              label: 'Paid This Month',
              value: summaryLoading ? '—' : (summaryData?.paidCount ?? 0),
              sub: 'billing paid',
              color: '#1D9E75',
            },
          ].map(card => (
            <div key={card.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <p className="text-xs text-gray-700 font-bold mb-1">{card.label}</p>
              <p className="text-2xl font-bold" style={{ color: card.color }}>{card.value}</p>
              <p className="text-xs text-gray-400 mt-1">{card.sub}</p>
            </div>
          ))}
        </div>

        {/* Summary Cards */}
        <SummaryCards data={summaryData} loading={summaryLoading} />
        {summaryError && (
          <div className="mb-4 text-center">
            <button onClick={fetchSummary} className="text-sm text-primary underline">
              Retry summary
            </button>
          </div>
        )}

        {/* Analytics Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Component Breakdown</h3>
            {summaryError ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-400 mb-2">Failed to load</p>
                <button onClick={fetchSummary} className="text-xs text-primary underline">Retry</button>
              </div>
            ) : (
              <ComponentBreakdownChart data={summaryData} loading={summaryLoading} />
            )}
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Top Enterprises by Revenue</h3>
            {selectedOpt && (
              <TopEnterprisesChart month={selectedOpt.month} year={selectedOpt.year} source={source} />
            )}
          </div>
        </div>

        {/* Enterprise Table */}
        {selectedOpt && (
          <EnterpriseTable month={selectedOpt.month} year={selectedOpt.year} onRefresh={fetchSummary} source={source} />
        )}
      </div>

      {/* Generate All Modal */}
      {showModal && (
        <GenerateAllModal
          onClose={() => setShowModal(false)}
          onConfirm={handleConfirmGenerate}
        />
      )}

    </div>
  );
};

export default InvoiceDashboard;
